import type { WidgetTemplateId, WidgetStyleConfig } from '@/types/ui/widget-styles';
import { deriveSecondary } from '@/pages/agent-interfaces/live-agent/components/GenieChatInputWeb';

interface WidgetTemplateThumbnailProps {
  templateId: WidgetTemplateId;
  primaryColor: string;
  config: WidgetStyleConfig;
}

/**
 * Renders a mini preview thumbnail for a widget template
 * Cleaner, more distinct visual for each template type
 */
export const WidgetTemplateThumbnail = ({
  templateId,
  primaryColor,
  config,
}: WidgetTemplateThumbnailProps) => {
  const isDark = config.theme.style === 'dark';
  const bgColor = isDark ? '#1f2937' : '#ffffff';

  // Simple website mockup background
  const WebsiteMockup = ({ children }: { children: React.ReactNode }) => (
    <div className="w-full h-full flex flex-col" style={{ backgroundColor: isDark ? '#111827' : '#f8fafc' }}>
      {/* Browser bar */}
      <div className="flex items-center gap-1.5 px-3 py-2 bg-white border-b border-gray-200">
        <div className="flex gap-1">
          <div className="w-2 h-2 rounded-full bg-[#ff5f57]" />
          <div className="w-2 h-2 rounded-full bg-[#febc2e]" />
          <div className="w-2 h-2 rounded-full bg-[#28c840]" />
        </div>
        <div className="flex-1 ml-3 h-3 bg-gray-100 rounded-full" />
      </div>
      {/* Page content */}
      <div className="flex-1 relative p-3">
        {/* Content skeleton */}
        <div className="space-y-2">
          <div className="h-2 bg-gray-200 rounded w-3/4" />
          <div className="h-2 bg-gray-200 rounded w-1/2" />
          <div className="h-8 bg-gray-100 rounded mt-3" />
        </div>
        {children}
      </div>
    </div>
  );

  const accentColor = deriveSecondary(primaryColor);

  switch (templateId) {
    case 'magic-v2':
      return (
        <WebsiteMockup>
          {/* v2 bubble — soft aura + 2 sparkle flecks + gradient bubble.
              Accent color derives from primary so the thumbnail reflects the
              actual customer's brand gradient, not Help Genie's pink. */}
          <div className="absolute bottom-3 right-3 w-9 h-9">
            {/* Soft brand aura behind the bubble */}
            <div
              className="absolute -inset-2 rounded-full"
              style={{
                background: `radial-gradient(circle, ${primaryColor}55 0%, ${accentColor}33 50%, transparent 75%)`,
                filter: 'blur(3px)',
              }}
            />
            {/* Sparkle top */}
            <div
              className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5"
              style={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
                clipPath:
                  'polygon(50% 0%, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0% 50%, 40% 40%)',
              }}
            />
            {/* Sparkle right */}
            <div
              className="absolute top-1/3 -right-1 w-1 h-1"
              style={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
                clipPath:
                  'polygon(50% 0%, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0% 50%, 40% 40%)',
              }}
            />
            {/* Bubble with gradient */}
            <div
              className="absolute inset-0 rounded-full shadow-md"
              style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)` }}
            />
          </div>
        </WebsiteMockup>
      );

    case 'classic':
      return (
        <WebsiteMockup>
          {/* Floating circle button */}
          <div
            className="absolute bottom-3 right-3 w-8 h-8 rounded-full shadow-lg flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <div className="w-5 h-5 rounded-full" style={{ backgroundColor: primaryColor }} />
            <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
          </div>
        </WebsiteMockup>
      );

    case 'compact':
      return (
        <WebsiteMockup>
          {/* Smaller rounded square button */}
          <div
            className="absolute bottom-3 right-3 w-6 h-6 rounded-lg shadow flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: primaryColor }} />
          </div>
        </WebsiteMockup>
      );

    case 'immersive':
      return (
        <WebsiteMockup>
          {/* Larger popup showing voice interface */}
          <div
            className="absolute bottom-2 right-2 w-16 h-20 rounded-lg shadow-xl overflow-hidden"
            style={{ backgroundColor: bgColor }}
          >
            {/* Header */}
            <div className="h-4 px-1.5 flex items-center" style={{ backgroundColor: `${primaryColor}15` }}>
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: primaryColor }} />
            </div>
            {/* Voice avatar area */}
            <div className="flex flex-col items-center justify-center p-1.5">
              <div
                className="w-8 h-8 rounded-full mb-1.5"
                style={{ backgroundColor: `${primaryColor}15` }}
              />
              <div className="w-10 h-2.5 rounded-full" style={{ backgroundColor: primaryColor }} />
            </div>
          </div>
        </WebsiteMockup>
      );

    case 'pill-button':
      return (
        <WebsiteMockup>
          {/* Pill button with text */}
          <div
            className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-lg"
            style={{ backgroundColor: primaryColor }}
          >
            <div className="w-3 h-3 rounded-full bg-white/30" />
            <span className="text-[8px] text-white font-semibold">Chat</span>
          </div>
        </WebsiteMockup>
      );

    case 'side-tab':
      return (
        <WebsiteMockup>
          {/* Vertical tab on right edge */}
          <div
            className="absolute top-1/2 right-0 -translate-y-1/2 px-1.5 py-3 rounded-l-md shadow-lg"
            style={{ backgroundColor: primaryColor }}
          >
            <span
              className="text-[7px] text-white font-semibold"
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
            >
              Help
            </span>
          </div>
        </WebsiteMockup>
      );

    case 'banner':
      return (
        <WebsiteMockup>
          {/* Full-width bottom banner */}
          <div
            className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-3 py-2 shadow-lg"
            style={{ backgroundColor: isDark ? '#374151' : 'white', borderTop: '1px solid #e5e7eb' }}
          >
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: `${primaryColor}20` }} />
              <span className="text-[7px] text-gray-600">Need help?</span>
            </div>
            <div
              className="px-2 py-0.5 rounded-full text-[6px] text-white font-medium"
              style={{ backgroundColor: primaryColor }}
            >
              Chat
            </div>
          </div>
        </WebsiteMockup>
      );

    case 'glassmorphism':
      return (
        <WebsiteMockup>
          {/* Frosted glass button */}
          <div
            className="absolute bottom-3 right-3 w-8 h-8 rounded-full shadow-lg flex items-center justify-center"
            style={{
              backgroundColor: 'rgba(255,255,255,0.6)',
              backdropFilter: 'blur(8px)',
              border: `1.5px solid ${primaryColor}30`,
            }}
          >
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: primaryColor }} />
          </div>
        </WebsiteMockup>
      );

    case 'dark-mode':
      return (
        <div className="w-full h-full flex flex-col bg-gray-900">
          {/* Dark browser bar */}
          <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-800 border-b border-gray-700">
            <div className="flex gap-1">
              <div className="w-2 h-2 rounded-full bg-[#ff5f57]" />
              <div className="w-2 h-2 rounded-full bg-[#febc2e]" />
              <div className="w-2 h-2 rounded-full bg-[#28c840]" />
            </div>
            <div className="flex-1 ml-3 h-3 bg-gray-700 rounded-full" />
          </div>
          {/* Dark page content */}
          <div className="flex-1 relative p-3">
            <div className="space-y-2">
              <div className="h-2 bg-gray-700 rounded w-3/4" />
              <div className="h-2 bg-gray-700 rounded w-1/2" />
              <div className="h-8 bg-gray-800 rounded mt-3" />
            </div>
            {/* Dark button */}
            <div
              className="absolute bottom-3 right-3 w-8 h-8 rounded-full shadow-lg flex items-center justify-center"
              style={{ backgroundColor: '#374151', border: `2px solid ${primaryColor}` }}
            >
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: primaryColor }} />
            </div>
          </div>
        </div>
      );

    default:
      return (
        <WebsiteMockup>
          <div
            className="absolute bottom-3 right-3 w-8 h-8 rounded-full shadow-lg flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <div className="w-5 h-5 rounded-full" style={{ backgroundColor: primaryColor }} />
          </div>
        </WebsiteMockup>
      );
  }
};
